import { useContext, useMemo } from 'react';
import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import SelectDropdown from 'app/components/SelectDropdown';
import { useRessource } from 'app/utils/ressource';
import {
  getBlueprintsRequest,
  handleBlueprintMessage
} from 'app/pages/BlueprintsPage/Blueprints/ressource';
import UserContext from 'app/common/UserContext';
import type { BlueprintObjectEventType } from 'app/pages/BlueprintsPage/types';
import EllipsisMiddle from 'app/components/EllipsisMiddle';

import style from './style.scss';

interface BaseProps {
  initialValue?: number;
  videogameSlug: VideogameSlug;
  disabled?: boolean;
  className?: string;
  label?: string;
  withIcon?: boolean;
  eventType?: BlueprintObjectEventType;
  withEllipsis?: boolean;
}

interface ClearableProps extends BaseProps {
  clearable: true;
  onChange: (value?: number) => void;
}

interface NotClearableProps extends BaseProps {
  clearable?: false;
  onChange: (value: number) => void;
}

type Props = ClearableProps | NotClearableProps;

const BlueprintSelector: FC<Props> = ({
  initialValue,
  videogameSlug,
  disabled,
  onChange,
  clearable,
  className,
  label,
  withIcon = true,
  eventType = 'match',
  withEllipsis = false
}) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { userType } = useContext(UserContext);

  const blueprintsRequest = useMemo(() => getBlueprintsRequest(userType), [userType]);
  const { status, data, withLoader } = useRessource({
    fetchInitialData: blueprintsRequest,
    topics: [{ topic: 'blueprints' }],
    handleMessage: handleBlueprintMessage
  });

  if (data === undefined && !withLoader) {
    return null;
  }
  if (withLoader) {
    return (
      <span className={classnames(style.button, className)}>{t('common-loading_blueprints')}</span>
    );
  }
  const addLabel = label || t('common-add_blueprint');
  const errorMessage = status === 'FAILED' ? t('common-blueprint_unavailable') : '';

  const blueprints = data || [];
  const options = blueprints
    .filter(({ videogame, event_type }) => videogame === videogameSlug && event_type === eventType)
    .map(({ id, name }) => ({
      label: withEllipsis ? (
        <EllipsisMiddle className={style.ellipsis} withTitle>
          {name}
        </EllipsisMiddle>
      ) : (
        name
      ),
      value: id
    }));

  const actions =
    clearable && options.length > 0
      ? [
          {
            key: 'clear',
            label: t('common-clear_blueprint'),
            onClick: () => {
              onChange();
            }
          }
        ]
      : [
          {
            key: 'new',
            label: t('common-create_new_one'),
            onClick: () => navigate(`/blueprints/${videogameSlug}/${eventType}/new`)
          }
        ];

  return (
    <SelectDropdown
      label={addLabel}
      variation="empty"
      options={options}
      value={initialValue || -1}
      onChange={onChange}
      loading={withLoader}
      error={errorMessage}
      actions={actions}
      disabled={disabled}
      className={classnames(style.button, className)}
      withIcon={withIcon}
      withPortal
      width={300}
    />
  );
};

BlueprintSelector.displayName = 'BlueprintSelector';

export default BlueprintSelector;
